/**
 * ActivityFeed – real-time agent decision timeline.
 * Driven by WebSocket agent events + analysis results.
 */
import { useRealtimeStore } from '@/hooks/useRealtime'
import { useAgents } from '@/hooks/useAgents'
import { Bot, Activity } from 'lucide-react'
import { clsx } from 'clsx'
import type { AgentStatus } from '@/lib/types'

const TYPE_COLORS: Record<string, string> = {
  behavioral_analysis: 'bg-purple/10 border-purple/20 text-purple-light',
  optimization:        'bg-blue/10   border-blue/20   text-blue-light',
  risk:                'bg-rose/10   border-rose/20   text-rose-light',
  market_analysis:     'bg-cyan/10   border-cyan/20   text-cyan',
  compliance:          'bg-amber/10  border-amber/20  text-amber',
  goals:               'bg-emerald/10 border-emerald/20 text-emerald',
  communication:       'bg-surface-card border-border text-text-muted',
}

interface FeedEntry {
  id: string
  agentName: string
  agentAcronym: string
  agentType: string
  task: string
  performance: number
}

// Real-data-only: AgentStatus rows carry no per-event timestamp, so the feed
// shows no time column. Never synthesise timestamps (e.g. Date.now() − i·12s)
// under a "Live" badge — that fabricates history.
function buildFeed(agents: AgentStatus[]): FeedEntry[] {
  return agents.map((a, i) => ({
    id: `${a.id}-${i}`,
    agentName: a.name,
    agentAcronym: a.acronym,
    agentType: a.type,
    task: a.current_task,
    performance: a.performance ?? 0,
  }))
}

export default function ActivityFeed({ maxItems = 8 }: { maxItems?: number }) {
  const rtAgents = useRealtimeStore(s => s.agentEvents)
  const { agents } = useAgents()
  const source = rtAgents.length > 0 ? rtAgents : agents
  const feed = buildFeed(source).slice(0, maxItems)

  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Activity size={15} className="text-text-muted" aria-hidden />
        <h2 className="font-semibold text-text-primary">Agent Activity</h2>
        <span className="badge badge-green ml-auto">Live</span>
      </div>

      {feed.length === 0 ? (
        <div className="py-8 text-center text-text-muted text-sm">
          <Bot size={24} className="mx-auto mb-2 opacity-30" aria-hidden />
          No agent activity yet
        </div>
      ) : (
        <ul className="space-y-3" aria-label="Agent activity timeline" aria-live="polite">
          {feed.map((entry, i) => {
            const colorCls = TYPE_COLORS[entry.agentType] ?? TYPE_COLORS.communication
            return (
              <li key={entry.id} className="flex items-start gap-3">
                {/* Timeline dot */}
                <div className="flex flex-col items-center shrink-0 pt-1">
                  <div
                    className={clsx('w-7 h-7 rounded-lg border flex items-center justify-center', colorCls)}
                    aria-hidden
                  >
                    <span className="text-[9px] font-bold">{entry.agentAcronym}</span>
                  </div>
                  {i < feed.length - 1 && (
                    <div className="w-px flex-1 bg-border mt-1 mb-0 min-h-4" aria-hidden />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-3">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-text-primary">{entry.agentName}</span>
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">{entry.task}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="h-1 flex-1 bg-surface-hover rounded-full max-w-20">
                      <div
                        className="h-full rounded-full bg-emerald"
                        style={{ width: `${entry.performance}%` }}
                        aria-label={`Performance: ${entry.performance.toFixed(0)}%`}
                      />
                    </div>
                    <span className="text-[10px] text-text-muted">{entry.performance.toFixed(0)}%</span>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
